import React from 'react';
import styles from "./ManualPricingTable.module.scss";
import {isCustom, StripePriceObject} from './stripePriceObject';

/**
 * Returns a React element containing the symbol for the given ISO currency
 * code followed by a non-breaking space.
 *
 * Supported: `"eur"` → €, `"usd"` → $.  Unknown codes render nothing.
 *
 * @param currency - Lowercase ISO 4217 currency code.
 */
export function getCurrencySymbol(currency: 'eur' | 'usd' | string) {
    return (
        <>
            {currency === 'eur' && <span>&euro;&nbsp;</span>}
            {currency === 'usd' && <span>&dollar;&nbsp;</span>}
        </>
    );
}
interface AmountSpanProps {
    decimals?: number;
    className?: string;
    small?: boolean;
    showMonthly?: boolean;
    price: {
        recurring?: StripePriceObject['recurring'];
        unit_amount: StripePriceObject['unit_amount'];
        currency?: StripePriceObject['currency'];
    },
    freeSpan?: boolean;
}

/**
 * Displays a formatted monetary amount derived from a Stripe price object.
 *
 * - `unit_amount === UNIT_AMOUNT.CUSTOM` or a string value → **"Custom solutions"**
 * - Computed amount is `0` and `freeSpan` is `true` → **"Free"**
 * - Otherwise renders the numeric amount with an optional currency symbol via
 *   {@link AmountSpan.Amount}.
 *
 * Set `showMonthly={true}` with a yearly recurring price to display the
 * effective monthly cost (annual `unit_amount` ÷ 12).
 *
 * @param price       - Stripe price data: `unit_amount`, optional `currency` and `recurring`.
 * @param decimals    - Decimal places to display (default `2`).
 * @param small       - Apply the smaller typographic variant.
 * @param showMonthly - Divide annual prices by 12 to show a monthly figure.
 * @param className   - Additional CSS class names.
 * @param freeSpan    - Render "Free" when the amount is zero (default `true`).
 */
export default function AmountSpan({ price: selectedPrice, showMonthly, decimals = 2, small, className = '', freeSpan = true }: AmountSpanProps) {
    if (isCustom(selectedPrice) || typeof selectedPrice.unit_amount === 'string') {
        return <b>Custom solutions</b>
    }
    const amount = selectedPrice.unit_amount / 100 / (showMonthly && selectedPrice.recurring?.interval === 'year' ? 12 : 1);
    return amount === 0 && freeSpan ? <span className={`${styles.Price}`}>Free</span>
        : (
        <span className={`${styles.Price} ${small ? styles.Small : ''} ${className}`}>
            <AmountSpan.Amount currency={selectedPrice.currency} amount={amount} decimals={decimals} />
        </span>
    );
}

/**
 * Renders a raw numeric amount with an optional currency symbol prefix.
 * Intended for use as `AmountSpan.Amount` inside a parent `AmountSpan`.
 *
 * @param amount   - The numeric value to display.
 * @param currency - ISO 4217 code used to pick the symbol (optional).
 * @param decimals - Decimal places (default `2`).
 */
AmountSpan.Amount = function Amount({ currency, amount, decimals = 2 }:  Pick<AmountSpanProps, 'decimals'> & { amount: number; currency?: string; decimals?: number }) {
    return (
      <span>
          {currency && getCurrencySymbol(currency)}
          <span>{amount.toFixed(decimals)}</span>
      </span>
    );
}
